import { useEffect, useState } from 'react'
import { type HistoryEntry, type Station, fetchHistory } from './samApi'

function HistoryThumb({ cover }: { cover: string }) {
  const [failed, setFailed] = useState(false)
  return (
    <span className="radio-player-history__thumb">
      {cover && !failed && (
        <img
          loading="lazy"
          alt=""
          src={cover}
          onError={() => setFailed(true)}
        />
      )}
    </span>
  )
}

type Props = {
  stationId: string
  token: string
  station: Station | null
  /** MediaItemId of the current track — a change refreshes the open list. */
  currentMediaItemId: string
  label: string
  disabled?: boolean
}

function RadioHistory({
  stationId,
  token,
  station,
  currentMediaItemId,
  label,
  disabled = false
}: Props) {
  const [open, setOpen] = useState(false)
  const [entries, setEntries] = useState<HistoryEntry[]>([])

  // biome-ignore lint/correctness/useExhaustiveDependencies(currentMediaItemId): reload the open list when the current track changes, so the track that just ended shows up
  useEffect(() => {
    if (!open || disabled) return
    let cancelled = false
    fetchHistory(stationId, token, station)
      .then((items) => {
        if (!cancelled) setEntries(items)
      })
      .catch(() => {
        /* keep whatever is already shown */
      })
    return () => {
      cancelled = true
    }
  }, [open, disabled, stationId, token, station, currentMediaItemId])

  return (
    <div className={`radio-player-history${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="radio-player-history__toggle"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {label}
      </button>
      {/* Grid 0fr→1fr collapse animates the height without measuring it. */}
      <div className="radio-player-history__collapse">
        <ul className="radio-player-history__list">
          {entries.map((entry, index) => (
            <li
              key={`${entry.title}-${index}`}
              className="radio-player-history__item"
            >
              <HistoryThumb cover={entry.cover} />
              <span className="radio-player-history__info">
                <span className="radio-player-history__title">
                  {entry.title}
                </span>
                {entry.artist && (
                  <span className="radio-player-history__artist">
                    {entry.artist}
                  </span>
                )}
              </span>
              {entry.playedAt && (
                <span className="radio-player-history__time">
                  {entry.playedAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default RadioHistory
